import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Zap, Shield, Layers, MousePointerClick, Settings, Rocket, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { SectionHeading } from '../components/ui/SectionHeading';
import { ProductCard } from '../components/ui/ProductCard';
import { MockDashboard } from '../components/ui/MockDashboard';
import { products } from '../data/products';
export function HomePage() {
  return <div className="overflow-hidden" data-id="element-301">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden" data-id="element-302">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-navy-100/50 via-white to-white" data-id="element-303"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-304">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-305">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-306">
              <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-6 leading-tight" data-id="element-307">
                Technology That Helps SMEs{' '}
                <span className="text-navy-600" data-id="element-308">Operate Smarter</span>
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed max-w-lg" data-id="element-309">
                Financial control, HR insights, reputation management, and legal
                automation — all in one integrated ecosystem.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-310">
                <Button size="lg" href="/contact" data-id="element-311">
                  Request Demo
                </Button>
                <Button variant="secondary" size="lg" href="/products" data-id="element-312">
                  Explore Products
                </Button>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 50
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative" data-id="element-313">
              <div className="absolute -inset-4 bg-gradient-to-r from-navy-500 to-teal-400 rounded-3xl blur-2xl opacity-20 animate-pulse" data-id="element-314"></div>
              <MockDashboard variant="main" className="transform lg:scale-110 lg:translate-x-12" data-id="element-315" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-24 bg-slate-50" data-id="element-316">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-317">
          <SectionHeading eyebrow="Our Ecosystem" title="Everything you need to grow" description="Modular tools that work perfectly alone, but even better together." className="mb-16" data-id="element-318" />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-319">
            {products.map((product, index) => <ProductCard key={product.id} product={product} index={index} data-id="element-320" />)}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white" data-id="element-321">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-322">
          <SectionHeading eyebrow="Why NexusFlow" title="Built for the modern SME" className="mb-16" data-id="element-323" />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-324">
            {[{
            icon: Building2,
            title: 'Built for SMEs',
            desc: 'Designed specifically for teams of 10-500 people, not enterprise giants.'
          }, {
            icon: Zap,
            title: 'Fast Implementation',
            desc: 'Get up and running in days with our guided onboarding process.'
          }, {
            icon: Shield,
            title: 'Secure & GDPR Ready',
            desc: 'Enterprise-grade security and compliance built into every tool.'
          }, {
            icon: Layers,
            title: 'Modular & Scalable',
            desc: 'Start with one tool and add more as your business grows.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="text-center" data-id="element-325">
                <div className="w-16 h-16 mx-auto bg-navy-50 rounded-2xl flex items-center justify-center mb-6 text-navy-600" data-id="element-326">
                  <item.icon size={32} data-id="element-327" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-328">
                  {item.title}
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-329">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-slate-900 text-white overflow-hidden relative" data-id="element-330">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 opacity-20" data-id="element-331">
          <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-navy-600 rounded-full blur-3xl" data-id="element-332"></div>
          <div className="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-teal-600 rounded-full blur-3xl" data-id="element-333"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-334">
          <SectionHeading eyebrow="How It Works" title="Simple onboarding process" light className="mb-20" data-id="element-335" />

          <div className="grid md:grid-cols-3 gap-12 relative" data-id="element-336">
            {/* Connecting Line */}
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-slate-700 z-0" data-id="element-337"></div>

            {[{
            icon: MousePointerClick,
            title: '1. Choose your tools',
            desc: 'Select the specific modules your business needs right now.'
          }, {
            icon: Settings,
            title: '2. We configure your system',
            desc: 'Our team helps set up your account and import existing data.'
          }, {
            icon: Rocket,
            title: '3. Go live in days',
            desc: 'Onboard your team and start seeing results immediately.'
          }].map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2
          }} className="relative z-10 text-center" data-id="element-338">
                <div className="w-24 h-24 mx-auto bg-slate-800 border-4 border-slate-900 rounded-full flex items-center justify-center mb-6 text-navy-400 shadow-xl" data-id="element-339">
                  <step.icon size={40} data-id="element-340" />
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-341">{step.title}</h3>
                <p className="text-slate-400 leading-relaxed" data-id="element-342">{step.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-navy-600 to-navy-800 text-white text-center" data-id="element-343">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-344">
          <motion.h2 initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-bold mb-8 tracking-tight" data-id="element-345">
            Ready to modernize your business operations?
          </motion.h2>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} data-id="element-346">
            <Button variant="secondary" size="lg" href="/contact" className="shadow-xl" data-id="element-347">
              Request a Demo
            </Button>
          </motion.div>
        </div>
      </section>
    </div>;
}